"use client";

import { useEffect, useState } from "react";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type EditableEntry,
  type SavedCitation,
  updateCitation,
} from "@/lib/bibliography";
import { DEFAULT_OUTPUT_LOCALE, type OutputLocale } from "@/lib/output-locales";

interface EditSavedCitationDialogProps {
  /** The citation being edited; `null` keeps the dialog closed. */
  citation: SavedCitation | null;
  /** Fallback output language when the saved record predates per-citation locales. */
  preferredLocale?: OutputLocale;
  onClose: () => void;
  onSaved: () => void;
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}

function Field({ id, label, value, onChange, placeholder, type }: FieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={type}
        value={value}
      />
    </div>
  );
}

/**
 * Correct a saved citation's metadata and regenerate its formatted text in the style
 * it was saved with. Shared by the studio and the compact dialog.
 */
export function EditSavedCitationDialog({
  citation,
  preferredLocale,
  onClose,
  onSaved,
}: EditSavedCitationDialogProps) {
  const bib = useBibStrings();
  const [form, setForm] = useState<EditableEntry>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (citation) {
      setForm({ ...citation.entry });
    }
  }, [citation]);

  function set(field: keyof EditableEntry) {
    return (value: string) =>
      setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    if (!citation) {
      return;
    }
    setSaving(true);
    try {
      const lang = citation.lang ?? preferredLocale ?? DEFAULT_OUTPUT_LOCALE;
      const res = await fetch("/api/cite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entry: form, style: citation.style, lang }),
      });
      if (!res.ok) {
        throw new Error(bib.regenerateFailed);
      }
      const data = await res.json();
      updateCitation(citation.id, {
        citation: data.citation,
        entry: form,
        lang,
      });
      onSaved();
      onClose();
    } catch (error) {
      console.error(bib.updateFailed, error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={!!citation}>
      <DialogContent className="sm:max-w-lg" showCloseButton={true}>
        <DialogHeader>
          <DialogTitle>{bib.editTitle}</DialogTitle>
          <DialogDescription>{bib.editBody}</DialogDescription>
        </DialogHeader>
        <form className="space-y-5" id="edit-saved-form" onSubmit={handleSave}>
          <Field
            id="edit-saved-title"
            label={bib.fieldTitle}
            onChange={set("title")}
            value={form.title ?? ""}
          />
          <Field
            id="edit-saved-author"
            label={bib.fieldAuthor}
            onChange={set("author")}
            placeholder="e.g. Smith, J. or John Smith"
            value={form.author ?? ""}
          />
          <Field
            id="edit-saved-issued"
            label={bib.fieldDate}
            onChange={set("issued")}
            placeholder="e.g. 2024 or 2024-01-15"
            value={form.issued ?? ""}
          />
          <Field
            id="edit-saved-url"
            label="URL"
            onChange={set("URL")}
            placeholder="https://..."
            type="url"
            value={form.URL ?? ""}
          />
          <Field
            id="edit-saved-publisher"
            label={bib.fieldPublisher}
            onChange={set("publisher")}
            value={form.publisher ?? ""}
          />
          <Field
            id="edit-saved-container"
            label={bib.sitePublication}
            onChange={set("containerTitle")}
            placeholder="e.g. BBC News, The New York Times"
            value={form.containerTitle ?? ""}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              id="edit-saved-volume"
              label={bib.fieldVolume}
              onChange={set("volume")}
              placeholder="e.g. 12"
              value={form.volume ?? ""}
            />
            <Field
              id="edit-saved-issue"
              label={bib.fieldIssue}
              onChange={set("issue")}
              placeholder="e.g. 3"
              value={form.issue ?? ""}
            />
            <Field
              id="edit-saved-page"
              label={bib.fieldPages}
              onChange={set("page")}
              placeholder="e.g. 45-67"
              value={form.page ?? ""}
            />
          </div>
          <Field
            id="edit-saved-doi"
            label="DOI"
            onChange={set("DOI")}
            placeholder="10.1000/xyz123"
            value={form.DOI ?? ""}
          />
        </form>
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            {bib.cancel}
          </Button>
          <Button disabled={saving} form="edit-saved-form" type="submit">
            {saving ? bib.saving : bib.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
